/**
 * Remembers which node each Dashboards tab was left on so leaving the page
 * and coming back reopens it instead of the "Select a dashboard" empty state
 * (AXI-5324). The nav rail and the tab strip both link at the bare surface
 * home, so without this a round trip always drops the open dashboard.
 *
 * The URL stays the only selection mechanism — this is a fallback consulted
 * when the user lands on a surface home with nothing selected, and the stored
 * path is re-validated against the loaded rows before it is restored so a
 * deleted or renamed node falls back to the empty state.
 *
 * Read/write style mirrors `DASHBOARDS_EXPLORER_OPEN_KEY` and
 * `orchestrator-ui-persistence.ts`: namespaced `ontology.*` keys, best-effort,
 * never throws.
 */

export type ExplorerSurface = 'dashboards' | 'templates'

export interface LastVisitedSelection {
  kind: 'item' | 'folder'
  path: string
}

const STORAGE_KEYS: Record<ExplorerSurface, string> = {
  dashboards: 'ontology.dashboards.lastVisited',
  templates: 'ontology.dashboards.templates.lastVisited'
}

function hasLocalStorage(): boolean {
  return typeof window !== 'undefined' && 'localStorage' in window
}

export function readLastVisited(surface: ExplorerSurface): LastVisitedSelection | null {
  if (!hasLocalStorage()) return null
  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(STORAGE_KEYS[surface])
  } catch {
    return null
  }
  if (raw === null) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { kind, path } = parsed as { kind?: unknown; path?: unknown }
    if (kind !== 'item' && kind !== 'folder') return null
    if (typeof path !== 'string' || path === '') return null
    return { kind, path }
  } catch {
    return null
  }
}

export function writeLastVisited(
  surface: ExplorerSurface,
  selection: LastVisitedSelection | null
): void {
  if (!hasLocalStorage()) return
  try {
    if (selection === null || selection.path === '') {
      window.localStorage.removeItem(STORAGE_KEYS[surface])
      return
    }
    window.localStorage.setItem(STORAGE_KEYS[surface], JSON.stringify(selection))
  } catch {
    // best-effort; persistence is a convenience, not a correctness requirement
  }
}
